/**
 * スコアとコンボの計算（純粋ロジック / Phaser 非依存）。
 *
 * 基本得点は「合体後に生成された料理のレベル」に応じて決まる。
 *   mode = 'formula' : base ^ level（既定 2^level）
 *   mode = 'item'    : ステージ定義の item.score
 *
 * コンボは、前回の合体から windowMs 以内に次の合体が起きた場合に継続する。
 */

export interface ScoreSystemOptions {
  mode: 'formula' | 'item';
  base: number;
  comboWindowMs: number;
  /** index 0 が 1コンボ目。超過分は最後の値を使用。 */
  comboMultipliers: readonly number[];
}

export interface MergeScoreResult {
  /** 加算された得点（倍率適用後）。 */
  gained: number;
  /** 倍率適用前の基本得点。 */
  base: number;
  /** 適用された倍率。 */
  multiplier: number;
  /** 適用後のコンボ数。 */
  combo: number;
  /** 累計スコア。 */
  total: number;
}

/** レベルに対する基本得点を求める純粋関数。 */
export function baseScoreForLevel(
  level: number,
  options: Pick<ScoreSystemOptions, 'mode' | 'base'>,
  itemScore?: number,
): number {
  if (options.mode === 'item') {
    return Math.max(0, Math.floor(itemScore ?? 0));
  }
  return Math.max(0, Math.floor(Math.pow(options.base, level)));
}

/** コンボ数に対する倍率を求める純粋関数（1コンボ目 = index 0）。 */
export function comboMultiplier(combo: number, multipliers: readonly number[]): number {
  if (multipliers.length === 0) return 1;
  if (combo <= 1) return multipliers[0];
  const index = Math.min(combo - 1, multipliers.length - 1);
  return multipliers[index];
}

export class ScoreSystem {
  private readonly options: ScoreSystemOptions;
  private currentScore = 0;
  private currentCombo = 0;
  private lastMergeAt = Number.NEGATIVE_INFINITY;
  private bestCombo = 0;

  constructor(options: ScoreSystemOptions) {
    this.options = options;
  }

  get score(): number {
    return this.currentScore;
  }

  get combo(): number {
    return this.currentCombo;
  }

  get maxCombo(): number {
    return this.bestCombo;
  }

  get multiplier(): number {
    return comboMultiplier(this.currentCombo, this.options.comboMultipliers);
  }

  /** 合体1回分のスコアを加算する。 */
  registerMerge(params: { level: number; itemScore?: number; now: number }): MergeScoreResult {
    const { level, itemScore, now } = params;
    const continues = now - this.lastMergeAt <= this.options.comboWindowMs;
    this.currentCombo = continues ? this.currentCombo + 1 : 1;
    this.bestCombo = Math.max(this.bestCombo, this.currentCombo);
    this.lastMergeAt = now;

    const base = baseScoreForLevel(level, this.options, itemScore);
    const multiplier = comboMultiplier(this.currentCombo, this.options.comboMultipliers);
    const gained = Math.round(base * multiplier);
    this.currentScore += gained;

    return {
      gained,
      base,
      multiplier,
      combo: this.currentCombo,
      total: this.currentScore,
    };
  }

  /** ボーナス得点を加算する（コンボへは影響しない）。 */
  addBonus(points: number): number {
    const value = Math.max(0, Math.round(points));
    this.currentScore += value;
    return this.currentScore;
  }

  /**
   * コンボ有効期限の更新。期限切れになった場合のみ true を返す。
   * 毎フレーム呼ぶ想定。
   */
  updateCombo(now: number): boolean {
    if (this.currentCombo === 0) return false;
    if (now - this.lastMergeAt > this.options.comboWindowMs) {
      this.currentCombo = 0;
      return true;
    }
    return false;
  }

  reset(): void {
    this.currentScore = 0;
    this.currentCombo = 0;
    this.bestCombo = 0;
    this.lastMergeAt = Number.NEGATIVE_INFINITY;
  }
}
